import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Final proof / CTA — massive sage canvas with a headline, an italic
// endorsement lifted straight from a top review, and a big primary
// action. The section is a "closing beat" before the footer.
export const FinalCta = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  return (
    <section
      ref={ref}
      className="relative bg-riversol-blue text-white py-24 sm:py-32 lg:py-44 overflow-hidden"
      data-testid="home-final-cta"
    >
      {/* grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
          className="text-[42px] sm:text-[48px] lg:text-[76px] xl:text-[92px] leading-[1.02] tracking-[-0.02em] max-w-[1100px] mx-auto"
        >
          The routine your{" "}
          <span className="italic text-white/70">skin has been waiting for.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.16 }}
          className="mt-8 italic text-[19px] sm:text-[22px] lg:text-[26px] leading-snug text-white/80 max-w-[720px] mx-auto"
        >
          “I've tried everything. Riversol is the only line that hasn't made
          my skin flare. Two years in and I'm still here.”
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.28 }}
          className="mt-4 font-mono text-[11px] tracking-[0.22em] uppercase text-white/50"
        >
          Verified 5-star review · Sofia M.
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-14 flex items-center justify-center gap-6 flex-wrap"
        >
          <a
            href="https://www.riversol.com/collections/all"
            data-testid="final-cta-primary"
            className="group inline-flex items-center gap-2 bg-white text-riversol-blue rounded-full px-10 py-4 text-[13px] tracking-[0.16em] uppercase font-medium hover:bg-white/90 transition-colors"
          >
            Shop by Concern
            <ArrowRight
              size={16}
              strokeWidth={1.8}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </a>
          <a
            href="https://www.riversol.com/pages/skin-analyzer"
            data-testid="final-cta-secondary"
            className="text-[13px] tracking-[0.16em] uppercase font-medium text-white underline underline-offset-[7px] decoration-white/40 hover:decoration-white transition-colors"
          >
            Or take the Skin Analyzer
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCta;
